
import { DepthFormat, invertDepthCompare } from "../CommonRenderTargets";
import { getBufferLayout2D, getPipelineHash2D, getMaterialVariations2D } from "../Pipelines";
import { Renderer } from "../Renderer";
import { VertexBuffer } from "../VertexBuffer";
import { getTargetBlendStateFromMaterial } from "../BlendStates";
import { BufferGeometry } from "three";
import { LineMaterial } from "../../render/LineMaterial";
import { LineMaterialManager } from "./LineMaterialManager";
import { $wgsl } from "../../wgsl-preprocessor/wgsl-preprocessor";
import { getObjectUniformsDeclaration } from "../main/uniforms/ObjectUniforms";
import { getLineUniformsDeclaration } from "./LineUniforms";
import hatch from "../chunks/hatch_pattern.wgsl";
import modulo from "../chunks/modulo.wgsl";
import lineShader from "./lineSS.wgsl";
import { FrameBindGroup } from "../main/FrameBindGroup";
import { getDepthBiasValues } from "../main/DepthBiasMap";
import { StateTrackingEncoder } from "../StateTrackingEncoder";

export class LinePipeline {
  /** @type {Renderer} */
  #renderer;
  /** @type {GPUDevice} */
  #device;

  /** @type {Map.<number, GPURenderPipeline>} */
  #pipelines = new Map();
  /** @type {GPUBindGroupLayout[]} */
  #bindGroupLayouts;
  /** @type {GPUColorTargetState[]} */
  #activeTargetsList;
  /** @type {VertexBuffer} */
  #vb;
  /** @type {LineMaterialManager} */
  #lineMatMan;

  /**
   * @param {Renderer} renderer
   */
  constructor(renderer) {
    this.#renderer = renderer;
    this.#device = renderer.getDevice();
    this.#vb = this.#renderer.getVB();
    this.#lineMatMan = new LineMaterialManager(renderer);
  }

  /**
   * @param {LineMaterial} material
   * @param {boolean} hasExtraParams
   * @param {boolean} categoryHighlightingEnabled
   * @param {boolean} categoryHighlightingFallback
   * @returns {string}
   */
  #getShader(material, hasExtraParams, categoryHighlightingEnabled = false, categoryHighlightingFallback = false) {
    // hasCategoryTarget: true when category highlighting uses a dedicated render target (not fallback mode)
    const hasCategoryTarget = categoryHighlightingEnabled && !categoryHighlightingFallback;
    return $wgsl(lineShader, {
      frameBindGroup: FrameBindGroup.getDeclaration(0),
      objectUniforms: getObjectUniformsDeclaration(1, false),
      lineUniforms: getLineUniformsDeclaration(2),
      lineMaterialUniforms: LineMaterialManager.getUniformsDeclaration(3),
      hatch,
      modulo,
      ...getMaterialVariations2D(material),
      hasExtraParams,
      categoryHighlighting: categoryHighlightingEnabled,
      categoryHighlightingFallback,
      hasCategoryTarget
    });
  }

  /**
   * @param {number} key
   * @param {BufferGeometry} geometry
   * @param {LineMaterial} material
   * @returns {GPURenderPipeline}
   */
  #createPipeline(key, geometry, material) {

    let pipeline;

    const categoryHighlightingEnabled = this.#renderer.isCategoryHighlightingEnabled();
    const hasExtraParams = geometry.vbstride === 12;
    const categoryHighlightingFallback = this.#renderer.isCategoryHighlightingFallback();
    let shader = this.#device.createShaderModule({
      label: 'line shader (lineSS)',
      code: this.#getShader(material, hasExtraParams, categoryHighlightingEnabled, categoryHighlightingFallback)
    });

    // Deep copy the targets so we can set our own blend state without side effects.
    // Creating a copy is fine as createPipeline isn't called frequently due to caching.
    const targets = structuredClone(this.#activeTargetsList);
    // Set color target blend state.
    targets[0].blend = getTargetBlendStateFromMaterial(material);

    const depthCompare = material.depthTest ? invertDepthCompare(material.depthFunc || 'less') : 'always';
    const sampleCount = this.#renderer.getMSAASampleCount();
    pipeline = this.#device.createRenderPipeline({
      label: `line pipeline, key: 0x${key.toString(16)}`,
      layout: this.#device.createPipelineLayout({
        bindGroupLayouts: this.#bindGroupLayouts
      }),
      vertex: {
        module: shader,
        entryPoint: 'vsmain',
        buffers: getBufferLayout2D(geometry)
      },
      fragment: {
        module: shader,
        entryPoint: 'psmain',
        targets: targets
      },
      primitive: {
        topology: 'triangle-list',
        cullMode: 'none'
      },

      depthStencil: {
        depthWriteEnabled: material.depthWrite,
        depthCompare,
        format: DepthFormat,
        ...getDepthBiasValues(material)
      },
      multisample: {
        count: sampleCount
      }
    });

    this.#pipelines.set(key, pipeline);

    return pipeline;
  }

  /** Clears all cached pipelines. Call when MSAA settings change. */
  resetPipelines() {
    this.#pipelines.clear();
  }

  /**
   * @param {GPUColorTargetState[]} targets
   */
  reset(targets) {
    this.#activeTargetsList = targets;
  }

  /**
   * @param {GPUBindGroupLayout} frameLayout
   * @param {GPUBindGroupLayout} objectLayout
   * @param {GPUBindGroupLayout} lineLayout
   */
  setLayouts(frameLayout, objectLayout, lineLayout) {
    this.#bindGroupLayouts = [
    frameLayout,
    objectLayout,
    lineLayout,
    this.#lineMatMan.getLayout()];

  }

  /**
   * @param {StateTrackingEncoder} passEncoder
   * @param {GPUBindGroup} frameBindGroup
   * @param {GPUBindGroup} objectBindGroup
   * @param {GPUBindGroup} lineBindGroup
   */
  setBindGroups(passEncoder, frameBindGroup, objectBindGroup, lineBindGroup) {
    passEncoder.setBindGroupIfNeeded(0, frameBindGroup);
    passEncoder.setBindGroupIfNeeded(1, objectBindGroup);
    passEncoder.setBindGroupIfNeeded(2, lineBindGroup);
  }

  /**
   * @param {StateTrackingEncoder} passEncoder
   * @param {number} objectIndex
   * @param {BufferGeometry} geometry
   * @param {LineMaterial} material
   */
  drawOne(passEncoder, objectIndex, geometry, material) {

    const key = getPipelineHash2D(geometry, material);

    let pipeline = this.#pipelines.get(key);
    if (!pipeline) {
      pipeline = this.#createPipeline(key, geometry, material);
    }

    passEncoder.setPipelineIfNeeded(pipeline);

    if (material.needsUpdate || !material.__gpuBindGroup) {
      this.#lineMatMan.initMaterial(material);
    }

    // Make sure raster and selection textures are up-to-date
    this.#lineMatMan.updateTextures(material);

    passEncoder.setBindGroupIfNeeded(3, material.__gpuBindGroup);

    this.#vb.draw(passEncoder, geometry, objectIndex);
  }

}